import { ModelWithId } from '@casops/store/apis/commonModels';
import { useAppSelector } from '@casops/store/hooks';
import { useMemo } from 'react';

interface UseLoadModelDataReturn<TModel extends ModelWithId> {
  isLoaded: boolean;
  item?: TModel;
}

// Generic type for RTK Query hooks that fetch models.
type UseModelQueryHook<TModel extends ModelWithId> = (
  arg: ModelWithId,
  options?: { skip?: boolean; refetchOnMountOrArgChange?: number },
) => {
  currentData?: TModel;
  isSuccess: boolean;
};

interface UseLoadModelDataProps<TModel extends ModelWithId> {
  useModelQueryHook: UseModelQueryHook<TModel>;
  refetchOnMountOrArgChange?: number;
  editMode?: boolean;
  id?: string | number;
}

export const useLoadModelData = <TModel extends ModelWithId>({
  useModelQueryHook,
  refetchOnMountOrArgChange = 10,
  editMode: editModeOverride,
  id: idOverride,
}: UseLoadModelDataProps<TModel>): UseLoadModelDataReturn<TModel> => {
  const { sidePanelModelId, sidePanelEditMode } = useAppSelector(state => state.global);

  const modelId = Number(idOverride ?? sidePanelModelId);
  const editMode = editModeOverride ?? sidePanelEditMode;

  const { currentData: item, isSuccess } = useModelQueryHook(
    { id: modelId },
    {
      skip: !editMode || isNaN(modelId),
      refetchOnMountOrArgChange,
    },
  );

  const isLoaded = useMemo<boolean>(() => {
    let isLoaded = false;

    // Confirm that the Edit form data-fetching requirements are met.
    if (editMode && isSuccess && item) {
      isLoaded = true;
    }
    // The create form has no further data-fetching requirements.
    else if (!editMode) {
      isLoaded = true;
    }

    return isLoaded;
  }, [editMode, isSuccess, item]);

  return {
    isLoaded,
    item,
  };
};

export default useLoadModelData;
